<script setup lang="ts">
  import { isId } from "@krainovsd/js-helpers";
  import {
    type TableDefaultColumnOptions,
    VButton,
    VCheckBox,
    VDivider,
    VInput,
    VPopover,
    VSkeleton,
  } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
  import type { RouteLocationNormalized } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, getCache, setCache } from "@/entities/cache";
  import { useCachedResource } from "@/entities/cache/use-cache-resource";
  import { useFavoritesStore } from "@/entities/favorites";
  import type { FavoriteReport } from "@/entities/favorites";
  import {
    DEFAULT_FILTERS,
    INDICATORS_BY_KEY,
    INDICATOR_OPTIONS,
    REPORT_STRING_GROUPS,
    REPORT_STRING_OPTION_BY_KEY,
    buildPreviousPeriodBody,
    buildReportBody,
    buildRequestStatisticsBody,
    compareReportIndicators,
    createReport,
    dateCellToFixedPeriod,
    dateFormatForSection,
    fetchReport,
    formatIndicatorValue,
    isReportSubstringDisabled,
    responseRowsToReportRows,
    savedFilterToReportFilters,
    updateReport,
    useReportStore,
  } from "@/entities/report";
  import type {
    ReportFilters as ReportFiltersType,
    ReportResponse,
    ReportRow,
    RequestStatisticsBody,
    SavedFilter,
  } from "@/entities/report";
  import { PAGES } from "@/entities/tech";
  import Breadcrumbs from "@/components/atoms/Breadcrumbs.vue";
  import ConfirmModal from "@/components/atoms/ConfirmModal.vue";
  import Sheet from "@/components/atoms/Sheet.vue";
  import ChartColumn from "@/components/icons/ChartColumn.vue";
  import ChevronRight from "@/components/icons/ChevronRight.vue";
  import Ellipsis from "@/components/icons/Ellipsis.vue";
  import Star from "@/components/icons/Star.vue";
  import IndicatorCard from "@/components/molecules/IndicatorCard.vue";
  import TableSkeleton from "@/components/molecules/TableSkeleton.vue";
  import { Table } from "@/components/organisms/Table";
  import { deepGroupByRow$ } from "@/components/organisms/Table/table.events";
  import type { DeepGroupByRowState, ITableColumn } from "@/components/organisms/Table/table.types";
  import ReportFilters from "./ReportFilters.vue";

  const { t } = useI18n();

  const route = useRoute();
  const router = useRouter();
  const reportStore = useReportStore();
  const favoritesStore = useFavoritesStore();
  const reportId = computed(() => (route.params.id ? Number(route.params.id) : null));

  // The report store is global, so its `reportName`, `savedFilter` and drill-down
  // `path` outlive this component. Wipe them at the very top of setup — before
  // any watches — so a fresh open of `/report` (no id) or a brand-new report id
  // never inherits the previous report's name or breadcrumb chain. (The
  // `reportId` watch below only fires on changes, not on the initial mount, so a
  // page left mid-drill and reopened on another report would otherwise keep the
  // stale path.) Watches below take over for in-place route changes within the
  // same component instance.
  reportStore.setReportName("");
  reportStore.setSavedFilter(null);
  reportStore.clearPath();

  // On-device favorite snapshot for the current report (used as an offline-first
  // source for the filters and table data).
  const favoriteSnapshot = ref<FavoriteReport | null>(null);

  onMounted(() => void favoritesStore.load());

  watch(
    reportId,
    async (id) => {
      await favoritesStore.load();
      favoriteSnapshot.value = id === null ? null : (favoritesStore.get(id) ?? null);
    },
    { immediate: true },
  );

  const isFavorite = computed(() => favoritesStore.isFavorite(reportId.value));

  // Currency symbol for "money" indicators, taken from the selected filters.
  const currencySymbol = computed(() => reportStore.currentFilters.currency?.symbol);

  const baselineFilters = ref<ReportFiltersType | null>(null);

  const filtersChanged = computed(() => {
    if (!baselineFilters.value) return false;
    return JSON.stringify(reportStore.currentFilters) !== JSON.stringify(baselineFilters.value);
  });

  // --- Display settings (KPI cards / table columns) -----------------------
  const cardIndicators = ref<Set<number>>(new Set());
  const tableIndicators = ref<Set<number>>(new Set());

  // Seed visibility from cache (persisted per report) or fall back to the
  // saved filter's indicator selection.
  watch(
    reportId,
    async (id) => {
      if (id === null) {
        cardIndicators.value = new Set(reportStore.currentFilters.indicators);
        tableIndicators.value = new Set(reportStore.currentFilters.indicators);
        return;
      }
      const cached = await getCache<{ cards: number[]; columns: number[] }>(
        CACHE_NAMESPACE.ReportDisplay,
        String(id),
      );
      if (cached?.data) {
        cardIndicators.value = new Set(cached.data.cards);
        tableIndicators.value = new Set(cached.data.columns);
      } else {
        cardIndicators.value = new Set(reportStore.currentFilters.indicators);
        tableIndicators.value = new Set(reportStore.currentFilters.indicators);
      }
    },
    { immediate: true },
  );

  function persistDisplay() {
    const id = reportId.value;
    if (id === null) return;
    void setCache(
      CACHE_NAMESPACE.ReportDisplay,
      String(id),
      { cards: [...cardIndicators.value], columns: [...tableIndicators.value] },
      { ttl: CACHE_TTL.Day },
    );
  }

  type SettingsSheet = "cards" | "columns";
  const settingsOpen = ref(false);
  const activeSheet = ref<SettingsSheet | null>(null);
  const draftIndicators = ref<Set<number>>(new Set());

  const SETTINGS_ITEMS: { key: SettingsSheet; label: string; icon: typeof Star }[] = [
    {
      key: "cards",
      label: t("page.report.settings.cards", {}, { default: "KPI-карточки" }),
      icon: Star,
    },
    {
      key: "columns",
      label: t("page.report.settings.columns", {}, { default: "Столбцы таблицы" }),
      icon: ChartColumn,
    },
  ];

  function openSheet(key: SettingsSheet) {
    settingsOpen.value = false;
    activeSheet.value = key;
    draftIndicators.value = new Set(key === "cards" ? cardIndicators.value : tableIndicators.value);
  }

  const activeSheetTitle = computed(
    () => SETTINGS_ITEMS.find((item) => item.key === activeSheet.value)?.label ?? "",
  );

  function toggleDraftIndicator(key: number) {
    const next = new Set(draftIndicators.value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    draftIndicators.value = next;
  }

  function applySettings() {
    if (activeSheet.value === "cards") cardIndicators.value = new Set(draftIndicators.value);
    else if (activeSheet.value === "columns")
      tableIndicators.value = new Set(draftIndicators.value);
    persistDisplay();
    activeSheet.value = null;
  }

  const allIndicators = computed(() =>
    INDICATOR_OPTIONS.map((option) => ({
      key: option.key,
      label: t(option.title.path, {}, { default: option.title.default }),
    })),
  );

  watch(reportId, () => {
    baselineFilters.value = null;
    // Switching to a different report invalidates the drill-down breadcrumb
    // chain — the saved snapshots belong to the previous report's view.
    reportStore.clearPath();
  });

  const DEFAULT_COLUMN: TableDefaultColumnOptions = {
    draggable: false,
    sortable: true,
    filterable: false,
    resizable: false,
    cellClass: ["common", "hCenter"],
    headerClass: ["common"],
  };

  // tanstack-table requires globally unique row ids across the whole table —
  // including sub-rows. The backend sometimes returns `id: 0` for grouping rows
  // (e.g. "Without mailing_type") and omits `id` on splitting children, so a
  // parent's `"0"` and a child's index `"0"` collided and froze the table on
  // expansion. We namespace each child by its parent's path id to keep them
  // unique.
  function getRowId(row: ReportRow, index: number, parent?: { id: string }) {
    const own = isId(row.id) ? row.id.toString() : index.toString();
    return parent ? `${parent.id}.${own}` : own;
  }

  // Always request ALL indicators from the API — the display settings
  // (cardIndicators / tableIndicators) control only what the user sees.
  const ALL_INDICATOR_KEYS = INDICATOR_OPTIONS.map((o) => o.key);

  const requestBody = computed<RequestStatisticsBody>(() => ({
    ...buildRequestStatisticsBody(reportStore.currentFilters),
    indicators: ALL_INDICATOR_KEYS,
  }));

  const savedFiltersCacheKey = computed(() =>
    reportId.value !== null ? `report-filters-${reportId.value}` : "",
  );

  const { data: savedFiltersData, refresh: refreshSavedFilters } = useCachedResource<SavedFilter>({
    namespace: CACHE_NAMESPACE.ReportFilters,
    key: savedFiltersCacheKey,
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      // Guard instead of asserting: the empty cache key already prevents this
      // fetcher from running while reportId is null (see savedFiltersCacheKey).
      const id = reportId.value;
      if (id === null) throw new Error("reportId is required to load saved filters");
      return reportStore.getReportFiltersById(id);
    },
  });

  // Use the network/cached response when present, otherwise the favorited
  // on-device snapshot — so a favorite hydrates its filters offline too.
  const effectiveSavedFilter = computed(
    () => savedFiltersData.value ?? favoriteSnapshot.value?.savedFilter ?? null,
  );

  watch(
    effectiveSavedFilter,
    (body) => {
      if (!body) return;
      if (reportId.value === null) return;
      if (baselineFilters.value) return;
      const filters = savedFilterToReportFilters(body);
      // Keep the full response so save/duplicate can merge changes onto the
      // fields the form doesn't manage (hash, sorting, other filter dimensions).
      reportStore.setSavedFilter(body);
      reportStore.setCurrentFilters(filters);
      reportStore.setReportName(body.name);
      baselineFilters.value = filters;
    },
    { immediate: true },
  );

  const cacheKey = computed(() => {
    if (reportId.value !== null && !baselineFilters.value) return "";
    // Include reportId so switching between /report/:id and /report (same
    // ReportPage instance is reused) changes the key and retriggers the fetch.
    return JSON.stringify({ report: reportId.value, body: requestBody.value });
  });

  const {
    data: reportData,
    isLoading: reportLoading,
    refresh: refreshReport,
  } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: cacheKey,
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchReport(requestBody.value),
  });

  // Same request as the table, shifted to the previous period of equal length
  // (e.g. 20–26.05 → 13–19.05) so its indicators can be compared.
  const previousPeriodBody = computed(() => buildPreviousPeriodBody(requestBody.value));

  const previousCacheKey = computed(() => {
    if (reportId.value !== null && !baselineFilters.value) return "";
    if (!previousPeriodBody.value) return "";
    // Hold the previous-period request until the table data is available, so it
    // never competes with — and therefore can't delay — the primary table
    // request. Accept the favorited snapshot as "available" too, so opening a
    // favorite offline (cache miss on `reportData`) still triggers the
    // previous-period cache lookup. An empty key keeps the resource idle.
    if (!reportData.value && !favoriteSnapshot.value?.reportData) return "";
    return JSON.stringify({ report: reportId.value, body: previousPeriodBody.value });
  });

  const {
    data: previousReportData,
    isLoading: previousReportLoading,
    refresh: refreshPreviousReport,
  } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: previousCacheKey,
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      // Guarded by the empty cache key above: this fetcher never runs while
      // the previous-period body is null.
      const body = previousPeriodBody.value;
      if (!body) throw new Error("previous period body is required to compare indicators");
      return fetchReport(body);
    },
  });

  // Fall back to the favorited on-device snapshot when the live request has no
  // data yet (e.g. offline cache miss), so a favorite opens without a network.
  const effectiveReportData = computed(
    () => reportData.value ?? favoriteSnapshot.value?.reportData ?? null,
  );
  const effectivePreviousReportData = computed(
    () => previousReportData.value ?? favoriteSnapshot.value?.previousReportData ?? null,
  );

  // Per-indicator totals for the current vs. previous period and the percent
  // difference between them, e.g. { sends: { firstValue, secondValue, diffPercent } }.
  const comparison = computed(() => {
    if (!effectiveReportData.value || !effectivePreviousReportData.value) return {};
    return compareReportIndicators(effectiveReportData.value, effectivePreviousReportData.value);
  });

  const reportInfo = computed(() => effectiveReportData.value?.reportInfo ?? null);
  const rows = computed<ReportRow[]>(() =>
    responseRowsToReportRows(effectiveReportData.value?.statistics ?? []),
  );

  // Show the skeleton only while the first response is still pending — once
  // there's data (incl. stale cache or the favorite snapshot) the real table
  // renders, so cached navigations don't flash a skeleton.
  const isTableLoading = computed(() => reportLoading.value && !effectiveReportData.value);

  const reportNameOverride = ref<string | null>(null);
  // Displayed name precedence:
  //   1. The user's in-flight rename (`reportNameOverride`).
  //   2. The most recent drill point — once the user drills, the original
  //      report's name no longer describes what's on screen, and it stays
  //      visible as the first breadcrumb anyway.
  //   3. The saved/loaded report name from the store.
  const reportName = computed(() => {
    if (reportNameOverride.value !== null) return reportNameOverride.value;
    const drill = reportStore.path[reportStore.path.length - 1]?.label;
    if (drill) return drill;
    return reportStore.reportName ?? "";
  });

  // The report store is global, so its `reportName` and `savedFilter` linger
  // across navigations (e.g. opening this page from CreateReportPage right after
  // viewing another report). Clear them when `reportId` changes so the header
  // doesn't flash the previous report's name until the new response arrives.
  watch(reportId, () => {
    reportNameOverride.value = null;
    reportStore.setReportName("");
    reportStore.setSavedFilter(null);
  });

  // True only while the user is actively renaming via the in-page rename
  // input. Drill-down replaces `reportName`'s displayed value too, but that
  // isn't an unsaved edit to the underlying report.
  const nameChanged = computed(
    () => reportNameOverride.value !== null && reportNameOverride.value !== reportStore.reportName,
  );

  // Controls (save / duplicate / cancel) show for an unsaved report (no id),
  // or when the filters or the report name differ from the saved baseline.
  const controlsVisible = computed(() => {
    if (reportId.value === null) return true;
    return filtersChanged.value || nameChanged.value;
  });

  const isMenuOpen = ref(false);
  const isRenaming = ref(false);
  const renameDraft = ref("");
  // VInput exposes the underlying <input> as `element` (not a focus method), so
  // we reach through it to move the cursor into the field.
  const renameInputRef = useTemplateRef<{ element: HTMLInputElement | null }>("renameInputRef");

  // Favorite/unfavorite the current report. Favoriting snapshots the saved filter
  // and the current table response to IndexedDB so it opens offline next time.
  async function toggleFavorite() {
    isMenuOpen.value = false;
    const id = reportId.value;
    if (id === null) return;

    if (favoritesStore.isFavorite(id)) {
      await favoritesStore.remove(id);
      favoriteSnapshot.value = null;
      return;
    }

    await favoritesStore.add({
      id,
      name: reportName.value,
      savedFilter: reportStore.savedFilter,
      reportData: effectiveReportData.value,
      previousReportData: effectivePreviousReportData.value,
    });
    favoriteSnapshot.value = favoritesStore.get(id) ?? null;
  }

  // Keep a favorited report's on-device snapshot current as fresh data arrives.
  watch([reportData, previousReportData, () => reportStore.savedFilter], () => {
    const id = reportId.value;
    if (id === null || !favoritesStore.isFavorite(id) || !reportData.value) return;
    void favoritesStore.syncData(id, {
      name: reportName.value,
      savedFilter: reportStore.savedFilter,
      reportData: reportData.value,
      // Keep the previously stored snapshot if the previous-period request
      // hasn't produced data yet, so we never blank out a good comparison.
      previousReportData:
        previousReportData.value ?? favoriteSnapshot.value?.previousReportData ?? null,
    });
  });

  async function startRename() {
    renameDraft.value = reportName.value;
    isRenaming.value = true;
    isMenuOpen.value = false;
    await nextTick();
    const input = renameInputRef.value?.element;
    input?.focus();
    // Select the prefilled name so the user can start typing a replacement.
    input?.select();
  }

  function commitRename() {
    const next = renameDraft.value.trim();
    if (next) reportNameOverride.value = next;
    isRenaming.value = false;
  }

  function cancelRename() {
    isRenaming.value = false;
  }

  watch(reportName, () => {
    if (!isRenaming.value) renameDraft.value = reportName.value;
  });

  // function handleShare() {
  //   console.log("share report");
  //   isMenuOpen.value = false;
  // }

  const isExporting = ref(false);

  // POST the current report body to `/statistics/file`; the helper reads the
  // response as a Blob, pulls the filename from `Content-Disposition`, and
  // triggers the browser download.
  async function handleExportXlsx() {
    if (isExporting.value) return;
    isMenuOpen.value = false;
    isExporting.value = true;
    try {
      await reportStore.downloadReport();
    } finally {
      isExporting.value = false;
    }
  }

  const isSaving = ref(false);

  // Set just before we re-issue the cancelled navigation (or before navigating
  // away after a successful save) so the unsaved-changes guard lets it through.
  // The full leave-guard wiring is set up further below.
  const allowLeave = ref(false);

  // Save merges the current filters onto the saved response and persists them:
  // PUT to update an existing report, or POST to create one when there's no id.
  async function saveChanges() {
    if (isSaving.value) return;
    const body = buildReportBody(
      reportStore.currentFilters,
      reportName.value,
      reportStore.savedFilter,
    );

    isSaving.value = true;
    try {
      if (reportId.value === null) {
        const { id } = await createReport(body);
        // Skip the unsaved-changes prompt — the user just persisted the report.
        allowLeave.value = true;
        await router.push({ name: PAGES.Report, params: { id: String(id) } });
        return;
      }

      await updateReport(reportId.value, body);
      // New baseline so the controls hide, and refetch the response (hash, etc.).
      baselineFilters.value = reportStore.currentFilters;
      reportStore.setReportName(reportName.value);
      reportNameOverride.value = null;
      void refreshSavedFilters({ force: true });
    } finally {
      isSaving.value = false;
    }
  }

  // Name for a copied report: keep an edited name as-is, otherwise append a
  // "(копия <date time>)" suffix so the copies stay distinct.
  function getDuplicateName() {
    if (nameChanged.value) return reportName.value;
    const date = dayjs().format("DD.MM.YYYY HH:mm");
    return `${reportName.value} ${t("page.report.controls.copySuffix", { date }, { default: "(копия {date})" })}`;
  }

  // Create a new report from the current merged filters; the create body carries
  // no id or creator, so the server assigns fresh ones. Returns the new id.
  async function createReportFromFilters(): Promise<number> {
    const body = buildReportBody(
      reportStore.currentFilters,
      getDuplicateName(),
      reportStore.savedFilter,
    );
    const { id } = await createReport(body);
    return id;
  }

  async function duplicateReport() {
    if (isSaving.value) return;
    isSaving.value = true;
    try {
      const id = await createReportFromFilters();
      // Skip the unsaved-changes prompt — the user just persisted (as a copy).
      allowLeave.value = true;
      await router.push({ name: PAGES.Report, params: { id: String(id) } });
    } finally {
      isSaving.value = false;
    }
  }

  function cancelChanges() {
    const baseline = baselineFilters.value ?? DEFAULT_FILTERS;
    reportStore.setCurrentFilters(baseline);
    // Revert a renamed title back to the saved name.
    reportNameOverride.value = null;
    void refreshReport({ force: true });
    void refreshPreviousReport({ force: true });
  }

  // --- Unsaved-changes guard on navigation away ---------------------------
  const hasUnsavedChanges = computed(() => filtersChanged.value || nameChanged.value);

  const leaveModalOpen = ref(false);
  const isLeaveSaving = ref(false);
  const pendingRoute = ref<RouteLocationNormalized | null>(null);
  // `allowLeave` (declared above) is used here too.

  onBeforeRouteLeave((to) => {
    if (allowLeave.value) {
      allowLeave.value = false;
      return true;
    }
    if (!hasUnsavedChanges.value) return true;
    pendingRoute.value = to;
    leaveModalOpen.value = true;
    return false;
  });

  async function proceedLeave() {
    const to = pendingRoute.value;
    leaveModalOpen.value = false;
    pendingRoute.value = null;
    if (!to) return;
    allowLeave.value = true;
    await router.push(to.fullPath);
  }

  // "Сохранить и выйти": persist onto the existing report (or create when there's
  // no id), then continue to the destination.
  async function leaveSaveAndExit() {
    if (isLeaveSaving.value) return;
    isLeaveSaving.value = true;
    try {
      if (reportId.value === null) {
        await createReportFromFilters();
      } else {
        const body = buildReportBody(
          reportStore.currentFilters,
          reportName.value,
          reportStore.savedFilter,
        );
        await updateReport(reportId.value, body);
      }
      await proceedLeave();
    } finally {
      isLeaveSaving.value = false;
    }
  }

  // "Сохранить как новый": create a copy, then continue to the destination.
  async function leaveSaveAsNew() {
    if (isLeaveSaving.value) return;
    isLeaveSaving.value = true;
    try {
      await createReportFromFilters();
      await proceedLeave();
    } finally {
      isLeaveSaving.value = false;
    }
  }

  // "Выйти без изменений": discard edits and continue to the destination.
  function leaveWithoutSaving() {
    void proceedLeave();
  }

  // Close icon / tap-out: keep the user on the page with their edits intact.
  function cancelLeave() {
    leaveModalOpen.value = false;
    pendingRoute.value = null;
  }
  // const providerCount = computed(() => reportInfo.value?.total ?? 0);
  const compareDates = computed(() => {
    const info = reportInfo.value;
    if (!info?.dateFrom || !info?.dateTo) return "";
    const from = dayjs(info.dateFrom).format("DD.MM.YYYY");
    const to = dayjs(info.dateTo).format("DD.MM.YYYY");
    return `${from} — ${to}`;
  });

  // Date sections — days(2), daysOfWeek(8), weeks(9), month(10) — group by
  // calendar date, read from the `date` field; every other section returns its
  // grouping value under the shared `name` field.
  const DATE_SECTIONS = new Set([2, 8, 9, 10]);
  const groupingKey = computed<keyof ReportRow>(() => {
    const section = reportStore.currentFilters.section;
    return section !== null && DATE_SECTIONS.has(section) ? "date" : "name";
  });

  // Same rule as `groupingKey`, but for child (splitting) rows: keyed off the
  // nested grouping, so a date sub-grouping reads/formats `date` and any other
  // reads `name`.
  const childGroupingKey = computed<keyof ReportRow>(() => {
    const nested = reportStore.currentFilters.groupByNested;
    return nested !== null && DATE_SECTIONS.has(nested) ? "date" : "name";
  });

  // Rows are twice the single-line height across every section, so the grouping
  // cell can wrap onto two lines.
  const BASE_ROW_HEIGHT = 40;
  const rowHeight = BASE_ROW_HEIGHT * 3;

  // Localised label of the selected section, shown as the grouping column
  // header and in the deep-group sheet subtitle.
  const sectionLabel = computed(() => {
    const section = reportStore.currentFilters.section;
    const sectionOption = section !== null ? REPORT_STRING_OPTION_BY_KEY[section] : undefined;
    return sectionOption
      ? t(sectionOption.title.path, {}, { default: sectionOption.title.default })
      : t("page.report.columns.date", {}, { default: "Дата" });
  });

  // The first, frozen column reflects the selected section: its key (which
  // field to read) and its name (the section label) both follow the filter.
  const groupingColumn = computed<ITableColumn<ReportRow>>(() => {
    const key = groupingKey.value;
    const childKey = childGroupingKey.value;

    return {
      key,
      name: sectionLabel.value,
      minWidth: 220,
      maxWidth: 220,
      sortable: false,
      leftFrozen: true,
      cellRender: {
        component: "expandable",
        props: {
          withDeepGroup: true,
          childNameField: childKey,
          childIsDate: childKey === "date",
          isDate: key === "date",
          formatDate: dateFormatForSection(reportStore.currentFilters.section),
          childFormatDate: dateFormatForSection(reportStore.currentFilters.groupByNested),
          // Non-date sections prefix the row id and wrap onto three lines.
          withId: key !== "date",
          multiline: key !== "date",
        },
      },
      headerRender: { component: "fixable" },
    };
  });

  // One column per visible table indicator. Controlled by the display settings
  // sheet (persisted to cache per report).
  const indicatorColumns = computed<ITableColumn<ReportRow>[]>(() =>
    [...tableIndicators.value].flatMap((indicatorKey) => {
      const indicator = INDICATORS_BY_KEY[indicatorKey];
      if (!indicator) return [];
      const key = indicator.name as keyof ReportRow;
      const format = "format" in indicator ? indicator.format : undefined;
      return [
        {
          key,
          name: t(indicator.title.path, {}, { default: indicator.title.default }),
          minWidth: 110,
          maxWidth: 200,
          cellRender: {
            component: "default",
            props: {
              customContent: (context) =>
                formatIndicatorValue(context.row.original[key], format, currencySymbol.value),
            },
          },
        },
      ];
    }),
  );

  // One comparison card per visible card indicator, controlled by the display
  // settings sheet.
  const compareIndicators = computed(() =>
    [...cardIndicators.value].flatMap((indicatorKey) => {
      const indicator = INDICATORS_BY_KEY[indicatorKey];
      if (!indicator) return [];
      return [
        {
          name: t(indicator.title.path, {}, { default: indicator.title.default }),
          field: indicator.name,
          format: "format" in indicator ? indicator.format : undefined,
        },
      ];
    }),
  );

  // Skeleton the compare cards while there's nothing real to show: either the
  // current-period data is still loading (no value yet), or the values are in
  // but the previous-period response — needed to compute the diff percent — is
  // still in flight (the "calculation" step). Once the previous request settles
  // (or there's no comparable previous period) the real cards render with
  // whatever comparison is available.
  const isCompareLoading = computed(() => {
    if (isTableLoading.value) return true;
    return Boolean(previousPeriodBody.value) && previousReportLoading.value;
  });

  // Match the skeleton card count to the selected indicators so the section
  // keeps its height; fall back to a sensible default before filters resolve.
  const compareSkeletonCount = computed(() => compareIndicators.value.length || 6);

  const columns = computed<ITableColumn<ReportRow>[]>(() => [
    groupingColumn.value,
    ...indicatorColumns.value,
  ]);

  // Per-column widths used to lay out the loading skeleton like the real table.
  const skeletonColumns = computed(() => columns.value.map((column) => column.minWidth ?? 120));

  const deepGroupBy = ref<DeepGroupByRowState | null>(null);

  deepGroupByRow$.useSubscribe((value) => {
    deepGroupBy.value = value;
  });

  // Clicked cell's key for the sheet title, date-formatted when the section
  // groups by date so it matches what the table cell shows.
  const deepGroupByKey = computed(() => {
    const key = deepGroupBy.value?.currentKey;
    if (!key) return "";
    if (groupingKey.value === "date") {
      const parsed = dayjs(key);
      if (parsed.isValid())
        return parsed.format(dateFormatForSection(reportStore.currentFilters.section));
    }
    return key;
  });

  // Sections that map to a filter dimension in ReportFilters; clicking the
  // CornerDownRight inside a row of one of these sections lets us filter by the
  // row's id (offers/campaigns/creatives/triggers/mailingTypes/providers).
  // Other sections (days/channels/landings) just change the grouping without
  // adding a filter.
  const SECTION_FILTER_FIELD: Partial<
    Record<number, "offers" | "campaigns" | "creatives" | "triggers" | "mailingTypes" | "providers">
  > = {
    0: "offers",
    1: "campaigns",
    4: "creatives",
    6: "triggers",
    12: "providers",
    14: "mailingTypes",
  };

  // Flat list of drill-down options derived from REPORT_STRING_GROUPS (same set
  // as the "Строки" picker). Each option corresponds to a section value.
  const drillOptions = computed(() =>
    REPORT_STRING_GROUPS.flatMap((group) =>
      group.options.map((option) => ({
        key: option.key,
        label: t(option.title.path, {}, { default: option.title.default }),
      })),
    ),
  );

  // The drilled-into option must differ from the current section and respect
  // the same date-hierarchy / triggers restrictions as the "Подстроки" picker.
  function isDrillDisabled(target: number) {
    const section = reportStore.currentFilters.section;
    if (section === target) return true;
    return isReportSubstringDisabled(section, target);
  }

  // Drill into a sub-grouping: save the current state to the breadcrumb path
  // and replace it with a new state that adds the clicked cell's id to the
  // current section's filter dimension and re-groups by the chosen one.
  function drillIntoSection(targetSection: number) {
    const state = deepGroupBy.value;
    if (!state) return;
    if (isDrillDisabled(targetSection)) return;

    const filters = reportStore.currentFilters;
    const currentSection = filters.section;
    const filterField = currentSection !== null ? SECTION_FILTER_FIELD[currentSection] : undefined;
    const original = (state.row as { original?: { id?: unknown; date?: unknown } }).original;
    const cellId = typeof original?.id === "number" ? original.id : null;
    // For a date section (days/weeks/month) the row has no filterable id; drill
    // narrows `period` to the clicked day/week/month instead.
    const fixedPeriod = dateCellToFixedPeriod(original?.date ?? state.currentKey, currentSection);

    // Snapshot the state we're leaving — the breadcrumb label is the cell's
    // displayed value, formatted like the table cell shows it.
    reportStore.pushPath({
      name: reportName.value,
      filters,
      label: deepGroupByKey.value || state.currentKey || sectionLabel.value,
    });

    // Build the next state.
    const next = { ...filters };
    if (filterField && cellId !== null && !next[filterField].includes(cellId)) {
      next[filterField] = [...next[filterField], cellId];
    }
    // Scope to the clicked date span when drilling out of a date section.
    if (fixedPeriod) {
      next.period = "fixPeriod";
      next.dateFrom = fixedPeriod.dateFrom;
      next.dateTo = fixedPeriod.dateTo;
    }
    next.section = targetSection;
    // Drop a substring the new section now forbids — same rule as
    // `isBreakdownDisabled` in ReportFilters' `selectSection` — so drilling can
    // never leave an invalid section/substring pair.
    if (
      next.groupByNested !== null &&
      (next.groupByNested === targetSection ||
        isReportSubstringDisabled(targetSection, next.groupByNested))
    ) {
      next.groupByNested = null;
    }
    reportStore.setCurrentFilters(next);

    deepGroupByRow$.next(null);
  }

  // Breadcrumb crumbs from the path stack; clicking restores that state. The
  // first crumb is the report itself (the root snapshot before any drilling),
  // so it shows the report's name; subsequent crumbs use the cell value the
  // user drilled into.
  const breadcrumbs = computed(() =>
    reportStore.path.map((entry, index) => ({
      label: index === 0 ? entry.name || entry.label : entry.label || entry.name,
    })),
  );

  function selectBreadcrumb(index: number) {
    reportStore.gotoPath(index);
  }
</script>

<template>
  <div :class="$style.base">
    <section v-if="breadcrumbs.length" :class="[$style.breadcrumbs, $style.pagePadding]">
      <Breadcrumbs :items="breadcrumbs" :current="sectionLabel" @select="selectBreadcrumb" />
    </section>
    <section :class="[$style.header, $style.pagePadding]">
      <div :class="$style.title">
        <VInput
          v-if="isRenaming"
          ref="renameInputRef"
          v-model="renameDraft"
          :class="$style.titleInput"
          size="large"
          @blur="commitRename"
          @keydown="
            (e: KeyboardEvent) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') cancelRename();
            }
          "
        />
        <h1 v-else :class="$style.titleText">{{ reportName }}</h1>
        <Star v-if="isFavorite" :class="$style.titleStar" :size="20" filled />
      </div>
      <section :class="$style.filters">
        <VPopover v-model="isMenuOpen" :class-name-positioner-content="$style.menu">
          <VButton><Ellipsis /></VButton>
          <template #content>
            <button type="button" :class="$style.menuItem" @click="startRename">
              {{ $t("page.report.menu.rename", {}, { default: "Переименовать" }) }}
            </button>
            <button
              v-if="reportId !== null"
              type="button"
              :class="$style.menuItem"
              @click="toggleFavorite"
            >
              {{
                isFavorite
                  ? $t("page.report.menu.unfavorite", {}, { default: "Удалить из избранного" })
                  : $t("page.report.menu.favorite", {}, { default: "В избранное" })
              }}
            </button>
            <VDivider :class="$style.menuDivider" />
            <!-- <button type="button" :class="$style.menuItem" @click="handleShare">
              {{ $t("page.report.menu.share", {}, { default: "Поделиться" }) }}
            </button> -->
            <button
              type="button"
              :class="$style.menuItem"
              :disabled="isExporting"
              @click="handleExportXlsx"
            >
              {{
                isExporting
                  ? $t("page.report.menu.exportingXlsx", {}, { default: "Подготовка файла…" })
                  : $t("page.report.menu.exportXlsx", {}, { default: "Экспорт XLSX" })
              }}
            </button>
          </template>
        </VPopover>
      </section>
    </section>
    <section v-if="controlsVisible" :class="$style.controls">
      <div :class="$style.controlsGroup">
        <VButton :loading="isSaving" @click="saveChanges">
          {{ $t("page.report.controls.save", {}, { default: "Сохранить" }) }}
        </VButton>
        <VButton
          v-if="reportId !== null"
          type="primary"
          :loading="isSaving"
          @click="duplicateReport"
        >
          {{ $t("page.report.controls.duplicate", {}, { default: "Дублировать" }) }}
        </VButton>
      </div>
      <VButton
        v-if="reportId !== null"
        :class="$style.controlsCancelButton"
        type="text"
        @click="cancelChanges"
      >
        {{ $t("page.report.controls.cancel", {}, { default: "Отменить изменения" }) }}
      </VButton>
    </section>
    <section :class="$style.pagePadding">
      <ReportFilters />
    </section>
    <VDivider />
    <section :class="[$style.compare, $style.pagePadding]">
      <div :class="$style.compareHeader">
        <!-- <span :class="$style.compareTitle">
          {{ $t("page.report.compareTitle", {}, { default: "Сравнение с предыдущим периодом" }) }}
        </span> -->
        <span :class="$style.compareDates"> {{ compareDates }} </span>
      </div>

      <div :class="$style.compareCards">
        <template v-if="isCompareLoading">
          <div
            v-for="n in compareSkeletonCount"
            :key="`compare-skeleton-${n}`"
            :class="$style.compareSkeletonCard"
            aria-hidden="true"
          >
            <VSkeleton type="text" :animation="true" :class="$style.compareSkeletonName" />
            <VSkeleton type="text" :animation="true" :class="$style.compareSkeletonValue" />
          </div>
        </template>
        <IndicatorCard
          v-for="indicator in compareIndicators"
          v-else
          :key="indicator.field"
          :name="indicator.name"
          :value="
            formatIndicatorValue(
              comparison[indicator.field]?.firstValue ?? 0,
              indicator.format,
              currencySymbol,
            )
          "
          :percent="comparison[indicator.field]?.diffPercent"
        />
      </div>
    </section>
    <section :class="[$style.table, $style.pagePadding]">
      <div :class="$style.tableToolbar">
        <VPopover v-model="settingsOpen" :class-name-positioner-content="$style.settingsMenu">
          <VButton type="text" :class="$style.settingsButton">
            {{ $t("page.report.settings.button", {}, { default: "Настройки отображения" }) }}
          </VButton>
          <template #content>
            <button
              v-for="item in SETTINGS_ITEMS"
              :key="item.key"
              type="button"
              :class="$style.settingsItem"
              @click="openSheet(item.key)"
            >
              <component :is="item.icon" :size="16" :class="$style.settingsItemIcon" />
              <span :class="$style.settingsItemLabel">{{ item.label }}</span>
              <ChevronRight :size="16" />
            </button>
          </template>
        </VPopover>
      </div>
      <TableSkeleton
        v-if="isTableLoading"
        :columns="skeletonColumns"
        :header-height="40"
        :row-height="rowHeight"
      />
      <Table
        v-else
        :rows="rows"
        :columns="columns"
        :default-column-options="DEFAULT_COLUMN"
        :get-row-id="getRowId"
        :get-sub-rows="(row) => row.children"
        :virtual-rows="true"
        :virtual-row-size="rowHeight"
        :header-height="40"
        :row-height="rowHeight"
        :with-total="false"
      />
    </section>
  </div>
  <Sheet
    :open="Boolean(deepGroupBy?.currentKey)"
    :title="`Разложить ${deepGroupByKey} по...`"
    :subtitle="`Текущий разрез: ${sectionLabel}. Выберите новый разрез.`"
    @update:open="deepGroupByRow$.next(null)"
  >
    <div :class="$style.deepGroupByRows">
      <div
        v-for="option in drillOptions"
        :key="option.key"
        :class="[
          $style.deepGroupByRow,
          isDrillDisabled(option.key) && $style.deepGroupByRowDisabled,
        ]"
        @click="drillIntoSection(option.key)"
      >
        <span>{{ option.label }}</span>
        <ChevronRight />
      </div>
    </div>
  </Sheet>
  <ConfirmModal
    :open="leaveModalOpen"
    :title="$t('page.report.leavePrompt.title', {}, { default: 'Несохранённые изменения' })"
    :subtitle="
      $t(
        'page.report.leavePrompt.subtitle',
        { name: reportName },
        { default: 'Что сделать с правками в отчёте «{name}»?' },
      )
    "
    @update:open="cancelLeave"
  >
    <div :class="$style.leaveActions">
      <VButton
        type="primary"
        size="large"
        :class="$style.confirmButton"
        :loading="isLeaveSaving"
        @click="leaveSaveAndExit"
      >
        {{ $t("page.report.leavePrompt.saveAndExit", {}, { default: "Сохранить и выйти" }) }}
      </VButton>
      <VButton
        size="large"
        :class="$style.confirmButton"
        :loading="isLeaveSaving"
        @click="leaveSaveAsNew"
      >
        {{ $t("page.report.leavePrompt.saveAsNew", {}, { default: "Сохранить как новый" }) }}
      </VButton>
      <VButton
        type="text"
        size="large"
        :class="$style.confirmButton"
        :disabled="isLeaveSaving"
        @click="leaveWithoutSaving"
      >
        {{ $t("page.report.leavePrompt.discard", {}, { default: "Выйти без изменений" }) }}
      </VButton>
    </div>
  </ConfirmModal>
  <Sheet :open="activeSheet !== null" :title="activeSheetTitle" @update:open="activeSheet = null">
    <div :class="$style.indicatorSheetContent">
      <div :class="$style.indicatorList">
        <div
          v-for="indicator in allIndicators"
          :key="indicator.key"
          :class="$style.indicatorRow"
          @click="toggleDraftIndicator(indicator.key)"
        >
          <VCheckBox
            :model-value="draftIndicators.has(indicator.key)"
            @click.stop
            @update:model-value="toggleDraftIndicator(indicator.key)"
          >
            {{ indicator.label }}
          </VCheckBox>
        </div>
      </div>
      <div :class="$style.sheetFooter">
        <VButton type="text" @click="activeSheet = null">
          {{ $t("page.report.settings.cancel", {}, { default: "Отменить" }) }}
        </VButton>
        <VButton type="primary" :class="$style.applyButton" @click="applySettings">
          {{ $t("page.report.settings.apply", {}, { default: "Применить" }) }}
        </VButton>
      </div>
    </div>
  </Sheet>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding-top: 20px;

    overflow: auto;

    display: flex;
    gap: 30px;
    flex-direction: column;
  }

  .pagePadding {
    padding-left: 20px;
    padding-right: 20px;
  }

  .header {
    padding-top: 16px;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
  }

  .title {
    flex: 1;
    min-width: 0;

    display: flex;
    align-items: center;
    gap: 8px;
  }

  .titleText {
    margin: 0;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .titleInput {
    flex: 1;
    min-width: 0;
  }

  .titleStar {
    flex-shrink: 0;
    color: var(--warning);
  }

  .menu {
    min-width: 200px;
    padding: 4px;

    display: flex;
    flex-direction: column;
    gap: 0;

    background-color: var(--popover);
  }

  .menuItem {
    padding: 8px 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .menuDivider {
    width: calc(100% + 8px);
    margin: 4px -4px;
  }

  .subTitle {
    color: var(--muted-foreground);
    font-weight: 500;

    font-size: 0.75rem; //xs
    line-height: 1rem; //xs
  }

  .filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .controls {
    // Pin to the top of the scrolling page (`.base`) so the actions stay
    // reachable while scrolling the report.
    position: sticky;
    top: 0;
    z-index: 30;

    padding: 0.5rem 1rem;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;

    // Opaque background so the scrolled content doesn't show through.
    --tw-backdrop-blur: blur(8px);
    backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness)
      var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate)
      var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate)
      var(--tw-backdrop-sepia);
    background-color: rgb(10 10 10 / 0.95);

    border-top: 1px solid var(--ksd-divider-color);
    border-bottom: 1px solid var(--ksd-divider-color);
  }

  .controlsCancelButton {
    font-size: 0.75rem;
    line-height: 1rem;

    color: var(--muted-foreground);
  }

  .controlsGroup {
    display: flex;
    gap: 0.5rem;
  }

  .compare {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .compareHeader {
    display: flex;
    justify-content: center;
  }

  .compareTitle {
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 600;
  }

  .compareDates {
    font-weight: 400;
    color: var(--muted-foreground);
  }

  .compareCards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  // Mirrors IndicatorCard's layout (name line + value line) so the skeleton
  // occupies the same footprint as the real cards.
  .compareSkeletonCard {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
  }

  .compareSkeletonName {
    width: 40%;
    height: 11px;
  }

  .compareSkeletonValue {
    width: 70%;
    height: 1.75rem;
  }

  .table {
    max-height: 600px;
    margin-bottom: 80px;
  }

  .deepGroupByRows {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .deepGroupByRow {
    padding: 0.5rem;

    display: flex;
    align-items: center;
    justify-content: space-between;

    cursor: pointer;

    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .deepGroupByRowDisabled {
    color: var(--muted-foreground);
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .breadcrumbs {
    padding-top: 12px;
  }

  .leaveActions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .confirmButton {
    width: 100%;

    justify-content: center;

    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .tableToolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 8px;
  }

  .settingsButton {
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .settingsMenu {
    min-width: 220px;
    padding: 4px;

    display: flex;
    flex-direction: column;

    background-color: var(--popover);
  }

  .settingsItem {
    padding: 10px 12px;

    display: flex;
    align-items: center;
    gap: 10px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .settingsItemIcon {
    flex-shrink: 0;
    color: var(--muted-foreground);
  }

  .settingsItemLabel {
    flex: 1;
    min-width: 0;
  }

  .indicatorSheetContent {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
  }

  .indicatorList {
    width: 100%;
    height: 100%;
    max-height: 100%;

    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    flex: 1;

    overflow: auto;
  }

  .indicatorRow {
    padding: 0.5rem;

    display: flex;
    align-items: center;

    cursor: pointer;
  }

  .sheetFooter {
    margin-top: 12px;
    padding-top: 12px;

    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;

    border-top: 1px solid var(--border-subtle);
  }

  .applyButton {
    background: var(--brand);
  }
</style>
